import {AttrsSchema, Component} from 'panel';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';

import {truncateMiddle} from 'common/util/string';
import {getModifierKeys} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ScrollBoxMountEvent} from 'common/widgets/scroll-control/types';

import {template} from './template';
import css from './index.styl?inline';
import {
  CellClickEvent,
  CellRenderContext,
  ColumnGroup,
  DataTableAttrs,
  SelectionChangeEvent,
  SortChangeEvent,
  SortTransformFunc,
  TableCell,
  TableColumn,
  TableRow,
  TableState,
  Theme,
} from './types';
import {Themes} from './themes';

const COMPONENT_NAME = `mp-data-table`;
const TRACK_EVENT = {
  LOADED: `Load`,
  CELL_CLICK: `Cell Click`,
  SELECTION_CHANGE: `Selection Change`,
  SORT_CHANGE: `Sort Change`,
  PROGRESSIVE_SCROLL: `Progressive Scroll`,
};
const INCREMENTAL_ROWS = 25;
const INITIAL_NUM_DISPLAY_ROWS = INCREMENTAL_ROWS * 4;
const COLUMN_TITLE_MAX_LENGTH = 100;

export const FUNNELS_CHECKBOX_CELL_WIDTH = 40;

/**
 * mp-data-table
 *
 * display mostly numeric data of a repetitive format densely in a scrolling container
 * with a sticky header and sticky columns.
 *
 * examples: retention data, funnels step data
 */
export class DataTable extends Component<TableState, DataTableAttrs> {
  static get attrsSchema(): AttrsSchema<DataTableAttrs> {
    return {
      ...super.attrsSchema,
      loading: {
        type: `boolean`,
      },
      'multi-select': {
        type: `boolean`,
        description: `Show checkboxes besides each row and allow select all / none of current filtered results`,
      },
      'selection-limit': {
        type: `number`,
        description: `Limit the number of selections allowed. Also disables shift-click.`,
      },
      'empty-message': {
        type: `string`,
        default: `This table is empty. Let's make a fresh start`,
      },
      'default-sort-field': {
        type: `string`,
        description: `Field by which table should be initially sorted. Updating this attribute will reset the sort to default-sort-field.`,
      },
      'default-sort-order': {
        type: `number`,
        default: 1,
        description: `Initial sort order, 1 for ascending, -1 for descending`,
      },
      'has-comparison': {
        type: `boolean`,
        description: `If comparison rows are interleaved in the rows passed to this component's rows prop`,
      },
      'max-body-height': {
        type: `string`,
        default: `100%`,
        description: `Max body height with units, more rows are loaded as user scrolls progressively`,
      },
      'num-sticky-rows': {
        type: `number`,
        default: 0,
        description: `Sticky rows that will not be sorted, that are to be placed at the start of the table, besides title row. These rows are to be indexed by [0, 1, ..., num-sticky-rows) in the table rows passed in by the user. By default, all rows are sortable.`,
      },
      'sticky-columns-count': {
        type: `number`,
        default: 2,
      },
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-table] <event>' bubbled events that can be listened for tracking with name as a property`,
      },
      theme: {
        type: `string`,
        enum: Object.values(Theme),
        default: Theme.Funnels,
      },
    };
  }

  private displayRowsCache: void | TableRow[];
  private loadingStartTimeMs: number;

  constructor() {
    super();
    this.loadingStartTimeMs = Date.now(); // unix milliseconds when loading started
    this.displayRowsCache = null; // cache of filtered + sorted rows, so scrolling is super smooth
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        rows: [],
        headers: [],
        hasBodyScrolled: false,
        numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS,
        sortField: null,
        sortOrder: 1,
        cellToolTip: null,
      },
      helpers: {
        COLUMN_TITLE_MAX_LENGTH,
        truncateMiddle,

        getRowsToDisplay: (): TableRow[] => this.getDisplayedRows().slice(0, this.state.numRowsToDisplay),

        getCellLabel: (row: TableRow<TableCell>, col: TableColumn): string => {
          const label = col.labelTransform ? col.labelTransform(row, col.field) : row[col.field];
          if (label === undefined || label === null) {
            return ``;
          }
          return label.toString();
        },

        getCellRenderContexts: (
          cols: TableColumn[],
          row: TableRow,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext[] => {
          return cols.map((col, colIdx) => this.helpers.getCellRenderContext(row, col, colIdx, rowIdx, totalRows));
        },

        getCellRenderContext: (
          row: TableRow,
          col: TableColumn,
          columnIdx: number,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext => ({
          field: col.field,
          label: this.helpers.getCellLabel(row, col),
          isComparison: this.attr(`has-comparison`) && rowIdx % 2 === 1,
          totalRows,
          columnIdx,
          rowIdx,
        }),

        getColumnsToDisplay: () => {
          return this.state.headers
            .map((group, groupIndex, allGroups) => {
              return group.columns.map((col, colIndex, allCols) => {
                const first = colIndex === 0;
                const last = colIndex === allCols.length - 1;
                const rightmost = last && groupIndex === allGroups.length - 1;
                return {
                  ...col,
                  first,
                  last,
                  rightmost,
                };
              });
            })
            .flat();
        },

        toggleSelected: (ev, row) => {
          ev.stopPropagation(); // so we don't also fire the rowClick event
          document.getSelection().empty(); // so text isn't highlighted on shift click or double click

          if (!row.isSelectDisabled) {
            const isSelected = !row.isSelected;
            const changed = [{isSelected, row}];

            this.dispatchEvent(
              new CustomEvent(`selectionChange`, {
                detail: {
                  changed,
                  isToggleAll: false,
                  rows: this.getDisplayedRows(),
                },
              }) as SelectionChangeEvent,
            );
            this.helpers.track(TRACK_EVENT.SELECTION_CHANGE, {isSelected, numChanged: changed.length});
          }
        },

        getSelectionStats: () => {
          const selectableRows = this._getSelectableDisplayedRows();
          const selectedRows = selectableRows.filter((row) => row.isSelected);
          const numSelectable = selectableRows.length;
          const numSelected = selectedRows.length;
          const limit = this.attr(`selection-limit`) || Number.POSITIVE_INFINITY;
          const rowLimit = this.attr(`has-comparison`) ? limit * 2 : limit;
          return {
            numSelectable,
            numSelected,
            allSelected: numSelectable > 0 && numSelected === numSelectable,
            atSelectionLimit: numSelected >= rowLimit,
          };
        },

        getCheckboxState: (item: TableRow<TableCell>) => {
          if (item.isSelected) {
            const options: any = {
              attrs: {
                checked: true,
                disabled: false,
              },
            };
            if (item.checkedColor) {
              options.attrs[`checked-color`] = item.checkedColor;
            }
            return options;
          }

          const {atSelectionLimit} = this.helpers.getSelectionStats();
          if (atSelectionLimit) {
            return {
              selectDisabledReason:
                item.selectDisabledReason || `You can't select more than ${this.attr(`selection-limit`)} items.`,
              attrs: {disabled: true, checked: false},
            };
          }

          return {
            selectDisabledReason: item.selectDisabledReason,
            attrs: {disabled: item.isSelectDisabled, checked: false},
          };
        },

        getCellItem: (row: TableRow<TableCell>, columnIdx: number) => {
          let cell;
          if (row.cells) {
            let cellColumnIdx = columnIdx - this.attr(`sticky-columns-count`);
            cellColumnIdx = this.attr(`multi-select`) ? cellColumnIdx - 1 : cellColumnIdx;
            cell = cellColumnIdx < row.cells.length && cellColumnIdx >= 0 ? row.cells[cellColumnIdx] : null;
          }
          return cell;
        },

        toggleAllSelected: () => {
          // partial -> none, none -> all, all -> none (like gmail)
          const isSelected = this.helpers.getSelectionStats().numSelected === 0;
          const changed = this._getSelectableDisplayedRows()
            .filter((row) => row.isSelected !== isSelected)
            .map((row) => ({isSelected, row}));
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                changed,
                isToggleAll: true,
                rows: this.getDisplayedRows(),
              },
            }) as SelectionChangeEvent,
          );
          this.helpers.track(TRACK_EVENT.SELECTION_CHANGE, {isSelected, numChanged: changed.length, toggleAll: true});
        },

        hasClickInteraction(column: TableColumn) {
          return column.onClickCallbackOverride || column.isSortable;
        },

        handleHeaderTitleClick: (column: TableColumn) => {
          const state = this.state;

          if (column.onClickCallbackOverride) {
            column.onClickCallbackOverride(column);
            return;
          }

          if (column.isSortable) {
            const sortField = column.field;
            // if same column is clicked, toggle sort order, otherwise keep it same
            const sortOrder = (state.sortField === sortField ? -1 : 1) * state.sortOrder;
            const sortDetail = {sortField, sortOrder};
            this.update(sortDetail);
            this.resetScroll();

            this._emitSortChange(true);
            this.helpers.track(TRACK_EVENT.SORT_CHANGE, {field: sortField, order: sortOrder});
          }
        },

        handleCellClick: (ev, row, field, context) => {
          this.dispatchEvent(
            new CustomEvent(`cellClick`, {
              detail: {
                row,
                field,
                context,
                target: ev.target,
                modifierKeys: getModifierKeys(ev),
              },
            }) as CellClickEvent,
          );
          this.helpers.track(TRACK_EVENT.CELL_CLICK, {field});
        },

        handleScroll: debounce(
          () => {
            // dispatch a single scroll event to invalidate any positioning of popovers
            this.dispatchEvent(new CustomEvent(`tableScroll`));

            const focused = this.el.querySelectorAll(`:focus`) || [];
            focused.forEach((f) => f.blur());
          },
          300,
          {leading: true},
        ),

        handleHeaderMouseEnter: (ev, column: TableColumn) => {
          const {x: cellX} = ev.target.getBoundingClientRect();
          const {x: wrapperX} = this.getBoundingClientRect();

          if (column.tooltip) {
            this.update({
              cellToolTip: {
                text: column.tooltip,
                left: cellX - wrapperX,
                // use the parent height because we know the event is fired
                // on a wrapped svg-icon within a sticky header cell
                top: ev.target.parentNode.clientHeight,
              },
            });
          }
        },

        handleHeaderMouseLeave: () => {
          this.update({cellToolTip: null});
        },

        setIntersectionEl: (el: HTMLElement) => {
          const observer = new IntersectionObserver(this.incrementDisplayedRows.bind(this), {
            root: this.el.querySelector(`.mp-table`),
          });

          observer.observe(el);

          this.onDisconnected(function () {
            observer.disconnect();
          });
        },

        track: (event, props) => {
          if (this.attr(`track-name`)) {
            this.dispatchEvent(
              new CustomEvent(`track`, {
                bubbles: true,
                composed: true,
                cancelable: true,
                detail: {
                  eventName: `[${COMPONENT_NAME}] ${event}`,
                  props: {
                    name: this.attr(`track-name`),
                    ...props,
                  },
                },
              }),
            );
          }
        },

        trackLoaded: () => {
          this.helpers.track(TRACK_EVENT.LOADED, {
            durationMs: Date.now() - this.loadingStartTimeMs,
            numRows: this.state.rows && this.state.rows.length,
          });
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    this.dispatchEvent(
      new CustomEvent(`scrollBoxMount`, {
        detail: {scrollBox: this.getScrollBox()},
        bubbles: true,
        composed: true,
      }) as ScrollBoxMountEvent,
    );

    requestAnimationFrame(() => {
      if (!this.attr(`loading`)) {
        this.helpers.trackLoaded();
      }

      if (this.attr(`default-sort-field`) || this.attr(`default-sort-order`)) {
        this._emitSortChange(false);
      }
    });
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `default-sort-field`) {
      // override state from attr if specified
      this.update({sortField: this.attr(attr)});
      this.resetScroll();
      this._emitSortChange(false);
    } else if (attr === `default-sort-order`) {
      this.update({sortOrder: this.attr(attr)});
      this.resetScroll();
      this._emitSortChange(false);
    } else if (attr === `loading`) {
      if (this.attr(attr)) {
        this.loadingStartTimeMs = Date.now();
      } else {
        this.helpers.trackLoaded();
      }
    }
  }

  public resetScroll() {
    this.displayRowsCache = null;
    this.update({numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS});
  }

  /** scroll the variable section of the table to align column $index with the end of the sticky section. */
  public scrollToDataColumn(index: number) {
    const target: HTMLElement = this.el.querySelector(
      `.table-header .data-columns-outer .title-cell:nth-of-type(${index})`,
    );
    const firstNonStickyCol: HTMLElement = this.el.querySelector(
      `.table-header .data-columns-outer .title-cell:first-child`,
    );
    const leftOffset = firstNonStickyCol ? firstNonStickyCol.offsetLeft : 0;
    this.getScrollBox().scroll(target.offsetLeft - leftOffset, 0);
  }

  public getScrollBox() {
    return this.el.querySelector(`.mp-table`);
  }

  public getTheme() {
    return Themes[this.attr(`theme`)];
  }

  /** @param val - pass via prop for perf reasons */
  set rows(val: Array<TableRow<TableCell>>) {
    const numRowsToDisplay = this.state.numRowsToDisplay || INITIAL_NUM_DISPLAY_ROWS;
    this.update({
      rows: val,
      // clamp numRowsToDisplay between INITIAL_NUM_DISPLAY_ROWS and val.length
      numRowsToDisplay: Math.max(Math.min(val.length, numRowsToDisplay), INITIAL_NUM_DISPLAY_ROWS),
    });
    this.displayRowsCache = null;
    this._emitSortChange(false);
  }

  getSelectColumn(): TableColumn[] {
    if (this.attr(`multi-select`)) {
      return [{field: `isSelected`, type: `checkbox`, sticky: true, width: FUNNELS_CHECKBOX_CELL_WIDTH}];
    } else {
      return [];
    }
  }

  set columnGroups(groups: Array<ColumnGroup | TableColumn>) {
    this.update({
      headers: groups.map((group, i) => {
        const maybeSelect = i === 0 ? this.getSelectColumn() : [];
        if (`columns` in group) {
          return {
            ...group,
            columns: maybeSelect.concat(group.columns),
          };
        } else {
          return {
            ...group,
            columns: maybeSelect.concat([group]),
          };
        }
      }),
    });

    this.displayRowsCache = null;
  }

  private _emitSortChange(isHeaderClick: boolean) {
    this.dispatchEvent(
      new CustomEvent(`sortChange`, {
        detail: {
          sortField: this.state.sortField,
          sortOrder: this.state.sortOrder,
          rows: this.getDisplayedRows(),
          isHeaderClick,
        },
      }) as SortChangeEvent,
    );
  }

  private incrementDisplayedRows() {
    const {numRowsToDisplay} = this.state;
    const totalRows = this.getDisplayedRows().length;

    if (numRowsToDisplay >= totalRows) {
      return;
    }

    this.update({
      numRowsToDisplay: Math.min(totalRows, numRowsToDisplay + INCREMENTAL_ROWS),
    });
  }

  private _rowSortFunc({
    a,
    b,
    sortOrder,
    sortTransform,
    sortField,
  }: {
    a: TableRow;
    b: TableRow;
    sortOrder: number;
    sortTransform?: SortTransformFunc<TableRow>;
    sortField?: string;
  }): number {
    if (sortTransform) {
      return sortOrder * (sortTransform(a, sortField, {sortOrder}) - sortTransform(b, sortField, {sortOrder}));
    }

    if (sortField) {
      const aVal = a[sortField]; // assume flat structure
      const bVal = b[sortField]; // assume flat structure
      const aType = typeof aVal;
      const bType = typeof bVal;

      if (aType === bType) {
        if (aType === `string`) {
          return sortOrder * aVal.localeCompare(bVal);
        } else if (aType === `number` || aType === `boolean`) {
          return sortOrder * (aVal - bVal);
        }
      }

      // truthy values come before falsey values
      return (aVal ? 0 : 1) - (bVal ? 0 : 1);
    }

    return 0;
  }

  private getDisplayedRows(): TableRow[] {
    // filter + sort is a significant overhead for tables with 1000s of rows
    // we memoize the display rows in a temporary cache so progressive scrolling is cheap
    if (this.displayRowsCache) {
      return this.displayRowsCache;
    }

    const {rows, sortField, sortOrder} = this.state;
    const columns = this.helpers.getColumnsToDisplay();

    const stickyRows = rows.slice(0, this.attr(`num-sticky-rows`));
    const sortableRows = rows.slice(this.attr(`num-sticky-rows`));
    let displaySortableRows = sortableRows;

    if (sortField) {
      const sortColumn = columns.find((col) => col.field === sortField);
      const sortTransform = sortColumn?.sortTransform;

      // single sort function is faster than lexicalCompose
      // sorting is usually the n*log(n) bottleneck so we want it to be fast
      if (this.attr(`has-comparison`)) {
        // sort row pairs together by the first row (current time period)
        const sortedPairs = chunk(displaySortableRows, 2).sort((pairA, pairB) => {
          return this._rowSortFunc({a: pairA[0], b: pairB[0], sortOrder, sortTransform, sortField});
        });
        displaySortableRows = flatten(sortedPairs);
      } else {
        displaySortableRows.sort((a, b) => {
          return this._rowSortFunc({a, b, sortOrder, sortTransform, sortField});
        });
      }
    }

    const displayRows = stickyRows.concat(displaySortableRows);
    return (this.displayRowsCache = displayRows);
  }

  private _getSelectableDisplayedRows(): Array<TableRow<TableCell>> {
    return this.getDisplayedRows().filter((row) => !row.isSelectDisabled);
  }
}

export const DataTableEl = defineAndMakeJsxFactory(COMPONENT_NAME, DataTable);
